import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';

import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { NgxSpinnerService } from 'ngx-spinner';
import { NzModalService } from 'ng-zorro-antd/modal';

import { UserRole } from '../../../core/enum';
import { DropdownInterface, PartnerInterfaceGlobal } from '../../../core/models';
import { AppOptionsService, AuthService, ErrorsHandlingService } from '../../../core/services';

import {
  CreateSspSourceComponent,
  TutorialComponent,
  CreateDspSourceComponent,
  DeactivationWarningComponent,
  DeleteSspSourceComponent,
  DeleteDspSourceComponent,
  LimitWarningComponent,
  EditSspSourceComponent,
  EditDspSourceComponent
} from '../modals';

import { SourceSchainInterface } from '../models';

@Injectable({
  providedIn: 'root'
})
export class GeneralSchainService {

  public tableData: SourceSchainInterface.ISourceSchain[] = [];

  public searchValue = '';

  public selectedTabIndex = 0;

  public dataSource: any[] = [];

  public userRole: UserRole = UserRole.viewer;

  public companiesSSP: DropdownInterface.ICompaniesDataGeneralSource[] = [];

  public sourceSchainForm: FormGroup;

  public tabItems = [
    {
      id: 1,
      name: 'DSP Side',
      active: false,
      disabled: false,
    },
    {
      id: 2,
      name: 'SSP Side',
      active: false,
      disabled: false,
    },
  ];


  private partnerSettings: PartnerInterfaceGlobal.IPartnerSettings;

  private apiUrl = '';

  private checkedCompanies: number[] = [];

  private isEditModalOpen = false;

  constructor(
    private modalService: NzModalService,
    private http: HttpClient,
    private errorsHandlingService: ErrorsHandlingService,
    private options: AppOptionsService,
    private fb: FormBuilder,
    public loadingService: NgxSpinnerService,
    private authService: AuthService,
  ) {
    this.apiUrl = this.options.getApiUrl();
  }

  public get isSourceSchain(): boolean {
    return this.sourceSchainForm.get('is_source_schain')?.value;
  }

  public onInitComponent(): void {
    this.getCompaniesHandler();
    this.createForm();
    this.getPartnerSettingsHandler();

    this.userRole = this.authService.userRole;
  }

  public isUserViewer(): boolean {
    return this.userRole === UserRole.viewer;
  }

  public onActivateSchainToggleHandler(value: boolean): void {
    this.loadingService.show();
    this.onActivateSchainToggleReq(value ? 1 : 0)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(res => {
        if (!res.success) {
          this.onShowDeactivationWarningModalHandler();
        } else {
          this.getPartnerSettingsHandler();
        }


      },
        error => this.errorsHandlingService.handleError(error));
  }

  public onShowTutorialModalHandler(): void {
    this.modalService.create({
      nzContent: TutorialComponent,
      nzClosable: false,
      nzMaskClosable: false,
      nzClassName: 'tutorial-modal',
      nzWrapClassName: 'tutorial-modal-wrap',
      nzStyle: { width: '650px', height: 'auto' },
      nzBodyStyle: { width: '650px', height: 'auto', overflow: 'hidden', padding: '32px' },
      nzCentered: true,
      nzFooter: null,
    });
  }

  public onChangeTabIndexHandler(tabId: number): void {
    this.selectedTabIndex = tabId;
    this.getTableListHandler(tabId === 1 ? 'ssp' : 'dsp');
  }

  public onOpenEditSourceSchainModalHandler(sourceId: number): void {
    if (this.isEditModalOpen) {
      return;
    }

    if (this.selectedTabIndex === 1) {
      this.onOpenEditSourceSchainSSPModalHandler(sourceId);
    } else {
      this.onOpenEditSourceSchainDspModalHandler(sourceId);
    }
  }

  public onOpenCreateSourceSchainModalHandler(): void {
    if (this.selectedTabIndex === 1 && this.tableData.length >= 100) {
      this.onShowLimitWarningModalHandler(100);
      return;
    } else if (this.selectedTabIndex === 1 && this.tableData.length < 100) {
      this.onShowCreateSspSourceModalHandler();
    }

    if (this.selectedTabIndex === 0 && this.tableData.length >= 100) {
      this.onShowLimitWarningModalHandler(100);
      return;
    } else if (this.selectedTabIndex === 0 && this.tableData.length < 100) {
      this.onShowCreateDspSourceModalHandler();
    }
  }

  public onShowDeleteSourceSchainModalHandler(sourceId: number): void {
    if (this.selectedTabIndex === 1) {
      this.onShowDeleteSspSourceModalHandler(sourceId);
    } else {
      this.onShowDeleteDspSourceModalHandler(sourceId);
    }
  }

  public onShowDeactivationWarningModalHandler(): void {
    const dialogRef = this.modalService.create({
      nzContent: DeactivationWarningComponent,
      nzClosable: false,
      nzMaskClosable: false,
      nzClassName: 'deactivation-warning-modal',
      nzWrapClassName: 'deactivation-warning-modal-wrap',
      nzStyle: { width: '570px', height: 'auto' },
      nzBodyStyle: { width: '570px', height: 'auto', overflow: 'hidden', padding: '0px' },
      nzCentered: true,
      nzFooter: null,
    });



    dialogRef.afterClose.subscribe((res: boolean) => {
      if (res) {
        this.loadingService.show();
        this.onActivateSchainToggleReq(0, 1)
          .pipe(finalize(() => this.loadingService.hide()))
          .subscribe(() => {
            this.getTableListHandler(this.selectedTabIndex === 1 ? 'ssp' : 'dsp');
          },
            error => this.errorsHandlingService.handleError(error));
      } else {
        this.sourceSchainForm.get('is_source_schain')?.patchValue(true, { emitEvent: true });
      }
    });
  }

  public getUploadFileRequest(file: File, type: string, fileName?: string, sourceId?: number): Observable<{ importId: number; }> {
    return this.onUploadFileRequest(file, type, fileName, sourceId);
  }

  public getCancelUploadSourceSchainRequest(importId: number, type: string): Observable<{ status: string }> {
    return this.onCancelUploadSourceSchainRequest(importId, type);
  }

  public getDeleteSourceSchainFileRequest(sourceId: number): Observable<{ status: string }> {
    return this.onDeleteSourceSchainFileRequest(sourceId);
  }

  public getDownloadSourceSchainFileRequest(sourceId: number): Observable<{ status: string, file: string }> {
    return this.onDownloadSourceSchainFileRequest(sourceId);
  }

  public getSendSourceSchainRequest(sourceId: number, companies?: number[], name?: string):
    Observable<{ status: string, data: SourceSchainInterface.ISourceSchain }> {
    return this.onSendSourceSchainRequest(sourceId, companies, name);
  }

  public getSourceSchainByIdRequest(sourceId: number): Observable<{ status: string, data: SourceSchainInterface.ISourceSchain }> {
    return this.onGetSourceSchainByIdRequest(sourceId);
  }

  public getSourceSchainListReq(type: string): Observable<{ status: string, data: SourceSchainInterface.ISourceSchain[] }> {
    return this.getSourceSchainListRequest(type);
  }

  public onGetExistingSourceSchainRequest(): Observable<{ sspFileCnt: number; dspFileCnt: number; }> {
    return this.getExistingSourceSchainRequest();
  }

  public getTableListHandler(type: string): void {
    this.isSourceSchain ? this.loadingService.show() : null;
    this.getSourceSchainListRequest(type)
      .pipe(finalize(() => this.isSourceSchain ? this.loadingService.hide() : null))
      .subscribe(res => {
        this.tableData = res.data;
        this.checkedCompanies = this.tableData.map(item => item.company).flat();
      },
        error => this.errorsHandlingService.handleError(error));
  }

  private onShowLimitWarningModalHandler(limit: number): void {
    this.modalService.create({
      nzContent: LimitWarningComponent,
      nzClosable: false,
      nzMaskClosable: false,
      nzClassName: 'deactivation-warning-modal',
      nzWrapClassName: 'deactivation-warning-modal-wrap',
      nzStyle: { width: '570px', height: 'auto' },
      nzBodyStyle: { width: '570px', height: 'auto', overflow: 'hidden', padding: '0px' },
      nzCentered: true,
      nzFooter: null,
      nzData: {
        limit,
      },
    });
  }


  private onShowCreateSspSourceModalHandler(): void {
    const dialogRef = this.modalService.create({
      nzContent: CreateSspSourceComponent,
      nzClosable: false,
      nzMaskClosable: false,
      nzClassName: 'full-screen-modal',
      nzWrapClassName: 'full-screen-modal-wrap',
      nzStyle: { top: '0px', width: '100%', height: '100%', maxWidth: 'none' },
      nzBodyStyle: { width: '100%', height: '100%', padding: '0' },
      nzCentered: true,
      nzFooter: null,
      nzData: {
        companies: this.companiesSSP,
        checkedCompanies: this.checkedCompanies,
      },
    });

    dialogRef.afterClose.subscribe(() => {
      this.getTableListHandler('ssp');
      this.getCompaniesHandler();
    });
  }

  private onShowCreateDspSourceModalHandler(): void {
    const dialogRef = this.modalService.create({
      nzContent: CreateDspSourceComponent,
      nzClosable: false,
      nzMaskClosable: false,
      nzClassName: 'tutorial-modal',
      nzWrapClassName: 'tutorial-modal-wrap',
      nzStyle: { width: '650px', overflow: 'hidden' },
      nzBodyStyle: { width: '650px', height: '620px', overflowY: 'scroll', padding: '0px' },
      nzCentered: true,
      nzFooter: null,
      nzData: {
        checkedCompanies: [],
        companies: [],
      },
    });


    dialogRef.afterClose.subscribe(() => {
      this.getTableListHandler('dsp');
      this.getCompaniesHandler();
    });
  }

  private onOpenEditSourceSchainSSPModalHandler(sourceId: number): void {
    this.checkedCompanies = this.tableData.filter(source => source.id !== sourceId).map(source => source.company).flat();

    this.isEditModalOpen = true;

    this.onGetSourceSchainByIdRequest(sourceId)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(res => {
        const dialogRef = this.modalService.create({
          nzContent: EditSspSourceComponent,
          nzClosable: false,
          nzMaskClosable: false,
          nzKeyboard: false,
          nzClassName: 'full-screen-modal',
          nzWrapClassName: 'full-screen-modal-wrap',
          nzStyle: { top: '0px', width: '100%', height: '100%', maxWidth: 'none' },
          nzBodyStyle: { width: '100%', height: '100%', padding: '0' },
          nzCentered: true,
          nzFooter: null,
          nzData: {
            sourceData: res.data,
            checkedCompanies: this.checkedCompanies,
            companies: this.companiesSSP,
          },
        });

        dialogRef.afterClose.subscribe((modalResult: boolean) => {
          this.isEditModalOpen = false;
          this.getTableListHandler('ssp');
          if (modalResult) {
            this.onDiscardSourceSchainFileHandler(sourceId);
          }
        });
      },
        error => {
          this.isEditModalOpen = false;
          this.errorsHandlingService.handleError(error);
        });
  }

  private onOpenEditSourceSchainDspModalHandler(sourceId: number): void {
    this.isEditModalOpen = true;

    this.onGetSourceSchainByIdRequest(sourceId)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(res => {
        const dialogRef = this.modalService.create({
          nzContent: EditDspSourceComponent,
          nzClosable: false,
          nzMaskClosable: false,
          nzKeyboard: false,
          nzClassName: 'tutorial-modal',
          nzWrapClassName: 'tutorial-modal-wrap',
          nzStyle: { width: '650px', overflow: 'hidden' },
          nzBodyStyle: { width: '650px', height: '620px', overflowY: 'scroll', padding: '0px' },
          nzCentered: true,
          nzFooter: null,
          nzData: {
            sourceData: res.data,
            checkedCompanies: [],
            companies: [],
          },
        });

        dialogRef.afterClose.subscribe((modalResult: boolean) => {
          this.isEditModalOpen = false;
          this.getTableListHandler('dsp');
          if (modalResult) {
            this.onDiscardSourceSchainFileHandler(sourceId);
          }
        });
      },
        error => {
          this.isEditModalOpen = false;
          this.errorsHandlingService.handleError(error);
        });
  }

  private onShowDeleteSspSourceModalHandler(sourceId: number): void {
    const sourceSSPDataLength = this.tableData.filter(source => source.id !== sourceId).length;

    const dialogRef = this.modalService.create({
      nzContent: DeleteSspSourceComponent,
      nzClosable: false,
      nzMaskClosable: false,
      nzClassName: 'deactivation-warning-modal',
      nzWrapClassName: 'deactivation-warning-modal-wrap',
      nzStyle: { width: '570px', height: 'auto' },
      nzBodyStyle: { width: '570px', height: 'auto', overflow: 'hidden', padding: '0px' },
      nzCentered: true,
      nzFooter: null,
      nzData: {
        sourceSSPDataLength: sourceSSPDataLength,
      },
    });



    dialogRef.afterClose.subscribe((res: boolean) => {
      if (res) {
        this.onDeleteSourceSchainFileHandler(sourceId, 1);
        this.checkInfoAndGetTableListHandler();
      }
    });
  }

  private onShowDeleteDspSourceModalHandler(sourceId: number): void {
    this.onDeleteSourceSchainRequest(sourceId)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe((res) => {
        if (typeof res.success === 'boolean' && !res.success) {
          const dspItems = res.dsp;
          const dialogRef = this.modalService.create({
            nzContent: DeleteDspSourceComponent,
            nzClosable: false,
            nzMaskClosable: false,
            nzClassName: 'deactivation-warning-modal',
            nzWrapClassName: 'deactivation-warning-modal-wrap',
            nzStyle: { width: '570px', height: 'auto' },
            nzBodyStyle: { width: '570px', height: 'auto', overflow: 'hidden', padding: '0px' },
            nzCentered: true,
            nzFooter: null,
            nzData: {
              dspItems: dspItems.join(', '),
            },
          });



          dialogRef.afterClose.subscribe((res: boolean) => {
            if (res) {
              this.onDeleteSourceSchainFileHandler(sourceId, 1);
              this.checkInfoAndGetTableListHandler();
            }
          });
        } else {
          this.checkInfoAndGetTableListHandler();
        }
      },
        error => this.errorsHandlingService.handleError(error));
  }

  private onDeleteSourceSchainFileHandler(sourceId: number, accept_source_schain_delete?: number): void {
    this.onDeleteSourceSchainRequest(sourceId, accept_source_schain_delete)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(() => {
        this.errorsHandlingService.showNotification('success', 'success', 'success', 'Source Schain successfully deleted!', null);
        this.checkInfoAndGetTableListHandler();
      },
        error => this.errorsHandlingService.handleError(error));
  }

  private checkInfoAndGetTableListHandler(): void {
    this.getExistingSourceSchainRequest()
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(res => {
        if (res.sspFileCnt > 0) {
          this.selectedTabIndex = 1;
        }

        if (res.dspFileCnt > 0) {
          this.selectedTabIndex = 0;
        }

        this.getTableListHandler(this.selectedTabIndex === 1 ? 'ssp' : 'dsp');
      });
  }

  private getPartnerSettingsHandler(): void {
    this.loadingService.show();
    this.authService.getAllPartnerInfo()
      .pipe(
        switchMap((res) => {
          this.partnerSettings = res.data as any;

          !this.partnerSettings.is_source_schain && this.loadingService.hide();

          this.updateForm();
          return this.getExistingSourceSchainRequest();
        })
      )
      .subscribe(res => {
        if (res.sspFileCnt > 0) {
          this.selectedTabIndex = 1;
        }

        if (res.dspFileCnt > 0) {
          this.selectedTabIndex = 0;
        }

        this.getTableListHandler(this.selectedTabIndex === 1 ? 'ssp' : 'dsp');
      },
        error => this.errorsHandlingService.handleError(error));
  }

  private onDiscardSourceSchainFileHandler(sourceId: number): void {
    this.onDiscardSourceSchainFileRequest(sourceId)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(() => {
      },
        error => this.errorsHandlingService.handleError(error));
  }
  private createForm(): void {
    this.sourceSchainForm = this.fb.group({
      is_source_schain: [false],
      searchValue: [null],
    });
  }

  private updateForm(): void {
    this.sourceSchainForm.patchValue({
      is_source_schain: this.partnerSettings.is_source_schain === 1 ? true : false,
    }, { emitEvent: false });
  }

  private getCompaniesHandler(): void {
    this.getCompaniesReq()
      .subscribe(res => {
        this.companiesSSP = res;
      });
  }

  private modifyCompaniesArray(res: DropdownInterface.ICompaniesDataGeneral[]): DropdownInterface.ICompaniesDataGeneralSource[] {
    return res.map((company) => ({
      id: company.id,
      name: company.name,
      value: company.id,
      label: company.name,
      checked: false,
      indeterminate: false,
      disabled: false,
      endpoints: [],
    }));
  }

  public disableCheckedCompanies(
    companiesChecked: number[],
    companies: DropdownInterface.ICompaniesDataGeneralSource[]
  ): DropdownInterface.ICompaniesDataGeneralSource[] {
    return companies.map(company => ({
      ...company,
      disabled: companiesChecked.includes(company.id)
    }));
  }

  public updateCompaniesForEditSSPSource(
    companiesChecked: number[],
    companies: DropdownInterface.ICompaniesDataGeneralSource[]
  ): DropdownInterface.ICompaniesDataGeneralSource[] {
    return companies.map(company => ({
      ...company,
      checked: companiesChecked.includes(company.id)
    }));
  }

  private getCompaniesReq(): Observable<DropdownInterface.ICompaniesDataGeneralSource[]> {
    const params = new HttpParams().append('without_deleted', true);

    return this.http.get<DropdownInterface.ICompaniesDataRes>(`${this.apiUrl}/endpoints/grouped-by-company`, { params: params })
      .pipe(map(res => {
        return this.modifyCompaniesArray(res.ssp);
      }),
        catchError(this.errorsHandlingService.processError));
  }

  private getSourceSchainListRequest(type: string): Observable<{ status: string, data: SourceSchainInterface.ISourceSchain[] }> {
    return this.http.post<{ status: string, data: SourceSchainInterface.ISourceSchain[] }>(`${this.apiUrl}/schain_sources/list/${type}`, {})
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  private onActivateSchainToggleReq(is_source_schain: number, accept_source_schain_turn_off?: number): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${this.apiUrl}/partner/settings/save/source_schain`, {
      is_source_schain,
      accept_source_schain_turn_off
    })
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  private onUploadFileRequest(file: File, type: string, fileName?: string, sourceId?: number): Observable<{ importId: number; }> {
    const formData: FormData = new FormData();
    formData.append('sources', file, file.name);
    if (fileName) {
      formData.append('file_name', fileName);
    }

    let url = `${this.apiUrl}/schain_sources/upload/${type}`;
    if (sourceId) {
      url = `${this.apiUrl}/schain_sources/upload/${type}/${sourceId}`;
    }

    return this.http.post<{ importId: number; }>(`${url}`, formData)
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  private onCancelUploadSourceSchainRequest(importId: number, type: string): Observable<{ status: string }> {
    return this.http.get<{ status: string }>(`${this.apiUrl}/schain_sources/cancel/${importId}/${type}`)
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  private onDeleteSourceSchainFileRequest(sourceId: number): Observable<{ status: string }> {
    return this.http.get<{ status: string }>(`${this.apiUrl}/schain_sources/clear/${sourceId}`)
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  private onDiscardSourceSchainFileRequest(sourceId: number): Observable<{ status: string, data: SourceSchainInterface.ISourceSchain }> {
    return this.http.post<{ status: string, data: SourceSchainInterface.ISourceSchain }>(`${this.apiUrl}/schain_sources/cancel/${sourceId}`, {})
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  private onDeleteSourceSchainRequest(
    sourceId: number,
    accept_source_schain_delete?: number
  ): Observable<{ success: boolean, dsp: number[], message: string }> {
    return this.http.post<{ success: boolean, dsp: number[], message: string }>(`${this.apiUrl}/schain_sources/delete/${sourceId}`, {
      accept_source_schain_delete
    })
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  private onDownloadSourceSchainFileRequest(sourceId: number): Observable<{ status: string, file: string }> {
    return this.http.get<{ status: string, file: string }>(`${this.apiUrl}/schain_sources/download/${sourceId}`)
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  private onGetSourceSchainByIdRequest(sourceId: number): Observable<{ status: string, data: SourceSchainInterface.ISourceSchain }> {
    return this.http.get<{ status: string, data: SourceSchainInterface.ISourceSchain }>(`${this.apiUrl}/schain_sources/list/${sourceId}`)
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  private getExistingSourceSchainRequest(): Observable<{ sspFileCnt: number; dspFileCnt: number; }> {
    return this.http.get<{ sspFileCnt: number; dspFileCnt: number; }>(`${this.apiUrl}/schain_sources/list/files/info`)
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  private onSendSourceSchainRequest(sourceId: number, companies?: number[], name?: string):
    Observable<{ status: string, data: SourceSchainInterface.ISourceSchain }> {

    let data: any = {};

    if (companies) {
      data.companies = companies;
    }

    if (name) {
      data.file_name = name;
    }


    return this.http.post<{ status: string, data: SourceSchainInterface.ISourceSchain }>(`${this.apiUrl}/schain_sources/save/${sourceId}`, data)
      .pipe(catchError(this.errorsHandlingService.processError));
  }
}

